// pages/ProductScannerInfoPage.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

/* ── Icon size token ─────────────────────────────────── */
const SZ = { width: 'var(--s-4)', height: 'var(--s-4)' } as React.CSSProperties;

const IconBarcode = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 5v14M6 5v14M9 5v14M11 5v14M14 5v14M17 5v14M21 5v14" />
  </svg>
);
const IconSpark = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v4" /><path d="M12 18v4" /><path d="M4.93 4.93 7.76 7.76" /><path d="M16.24 16.24l2.83 2.83" />
    <path d="M2 12h4" /><path d="M18 12h4" /><path d="M4.93 19.07 7.76 16.24" /><path d="M16.24 7.76l2.83-2.83" />
  </svg>
);
const IconCompare = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 3H5a2 2 0 0 0-2 2v5" />
    <path d="M20 14v5a2 2 0 0 1-2 2h-5" />
    <path d="m3 21 6-6" />
    <path d="m21 3-6 6" />
  </svg>
);
const IconLink = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l2-2a5 5 0 1 0-7.07-7.07l-1.2 1.2" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-2 2a5 5 0 1 0 7.07 7.07l1.2-1.2" />
  </svg>
);
const IconStack = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 2 9 5-9 5L3 7l9-5Z" /><path d="m3 17 9 5 9-5" /><path d="m3 12 9 5 9-5" />
  </svg>
);
const IconRule = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h10M7 12h7M7 16h10" />
  </svg>
);
const IconShield = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 3 6v6c0 5 3.8 9 9 10 5.2-1 9-5 9-10V6l-9-4Z" />
  </svg>
);
const IconTune = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 4H8" /><path d="M21 12H8" /><path d="M21 20H8" />
    <circle cx="6" cy="4" r="2" /><circle cx="6" cy="12" r="2" /><circle cx="6" cy="20" r="2" />
  </svg>
);
const IconChevron = () => (
  <svg viewBox="0 0 24 24" style={SZ} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/* ── SVG Entity Relationship Diagram ─────────────────── */
function DataModelDiagram() {
  return (
    <div className="card card--bordered card--flat" style={{ padding: 'var(--s-4)' }}>
      <div className="t-small" style={{ fontWeight: 600, color: 'var(--c-charcoal)', marginBottom: 'var(--s-2)' }}>
        Data model (simplified)
      </div>

      <svg
        viewBox="0 0 980 460"
        style={{ width: '100%', height: 'auto', color: 'var(--c-charcoal)' }}
        aria-hidden="true"
      >
        <title>Products, Barcodes, Ingredients, Rules, and Evidence relationships</title>
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
          </marker>
          <filter id="soft" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.12" />
          </filter>
        </defs>

        {/* Products */}
        <g transform="translate(32,24)" filter="url(#soft)">
          <rect x="0" y="0" width="260" height="140" rx="12" style={{ fill: 'var(--c-cream)' }} stroke="currentColor" strokeOpacity="0.3" />
          <text x="16" y="28" fontSize="14" fontWeight="700">Products</text>
          <line x1="0" y1="40" x2="260" y2="40" stroke="currentColor" strokeOpacity="0.15" />
          <text x="16" y="64" fontSize="12">product_id (PK)</text>
          <text x="16" y="84" fontSize="12">brand, name, category</text>
          <text x="16" y="104" fontSize="12">image_url …</text>
        </g>

        {/* Barcodes */}
        <g transform="translate(328,24)" filter="url(#soft)">
          <rect x="0" y="0" width="260" height="140" rx="12" style={{ fill: 'var(--c-cream)' }} stroke="currentColor" strokeOpacity="0.3" />
          <text x="16" y="28" fontSize="14" fontWeight="700">Barcodes</text>
          <line x1="0" y1="40" x2="260" y2="40" stroke="currentColor" strokeOpacity="0.15" />
          <text x="16" y="64" fontSize="12">gtin (PK)</text>
          <text x="16" y="84" fontSize="12">product_id (FK)</text>
        </g>

        {/* EvidenceSources */}
        <g transform="translate(624,24)" filter="url(#soft)">
          <rect x="0" y="0" width="324" height="140" rx="12" style={{ fill: 'var(--c-cream)' }} stroke="currentColor" strokeOpacity="0.3" />
          <text x="16" y="28" fontSize="14" fontWeight="700">EvidenceSources</text>
          <line x1="0" y1="40" x2="324" y2="40" stroke="currentColor" strokeOpacity="0.15" />
          <text x="16" y="64" fontSize="12">source_id (PK)</text>
          <text x="16" y="84" fontSize="12">type, citation, freshness</text>
        </g>

        {/* ProductIngredients */}
        <g transform="translate(32,232)" filter="url(#soft)">
          <rect x="0" y="0" width="320" height="172" rx="12" style={{ fill: 'var(--c-cream)' }} stroke="currentColor" strokeOpacity="0.3" />
          <text x="16" y="28" fontSize="14" fontWeight="700">ProductIngredients</text>
          <line x1="0" y1="40" x2="320" y2="40" stroke="currentColor" strokeOpacity="0.15" />
          <text x="16" y="64" fontSize="12">product_id (FK)</text>
          <text x="16" y="84" fontSize="12">ingredient_id (FK)</text>
          <text x="16" y="104" fontSize="12">position, confidence</text>
          <text x="16" y="124" fontSize="12">original_text</text>
        </g>

        {/* Ingredients */}
        <g transform="translate(392,232)" filter="url(#soft)">
          <rect x="0" y="0" width="268" height="172" rx="12" style={{ fill: 'var(--c-cream)' }} stroke="currentColor" strokeOpacity="0.3" />
          <text x="16" y="28" fontSize="14" fontWeight="700">Ingredients</text>
          <line x1="0" y1="40" x2="268" y2="40" stroke="currentColor" strokeOpacity="0.15" />
          <text x="16" y="64" fontSize="12">ingredient_id (PK)</text>
          <text x="16" y="84" fontSize="12">INCI, aliases, CAS/EC</text>
          <text x="16" y="104" fontSize="12">function, tags</text>
        </g>

        {/* Rules */}
        <g transform="translate(692,232)" filter="url(#soft)">
          <rect x="0" y="0" width="256" height="172" rx="12" style={{ fill: 'var(--c-cream)' }} stroke="currentColor" strokeOpacity="0.3" />
          <text x="16" y="28" fontSize="14" fontWeight="700">Rules</text>
          <line x1="0" y1="40" x2="256" y2="40" stroke="currentColor" strokeOpacity="0.15" />
          <text x="16" y="64" fontSize="12">rule_id (PK)</text>
          <text x="16" y="84" fontSize="12">domain, weight, rationale</text>
        </g>

        {/* Relations */}
        <line x1="292" y1="94" x2="328" y2="94" stroke="currentColor" strokeOpacity="0.6" markerEnd="url(#arrow)" />
        <text x="300" y="84" fontSize="12">1</text>
        <text x="316" y="112" fontSize="12">n</text>

        <line x1="162" y1="164" x2="162" y2="232" stroke="currentColor" strokeOpacity="0.6" markerEnd="url(#arrow)" />
        <text x="168" y="188" fontSize="12">1</text>
        <text x="168" y="216" fontSize="12">n</text>

        <line x1="352" y1="318" x2="392" y2="318" stroke="currentColor" strokeOpacity="0.6" markerEnd="url(#arrow)" />
        <text x="360" y="308" fontSize="12">n</text>
        <text x="380" y="336" fontSize="12">1</text>

        <line x1="692" y1="292" x2="660" y2="292" stroke="currentColor" strokeOpacity="0.5" strokeDasharray="4 3" markerEnd="url(#arrow)" />
        <text x="622" y="284" fontSize="12">targets</text>

        <line x1="786" y1="164" x2="786" y2="232" stroke="currentColor" strokeOpacity="0.5" strokeDasharray="4 3" markerEnd="url(#arrow)" />
        <text x="794" y="204" fontSize="12" transform="rotate(90 794,204)">cites</text>
      </svg>

      <p className="t-small t-mute" style={{ marginTop: 'var(--s-3)' }}>
        Solid lines = relational links (with cardinalities). Dashed lines = references used during scoring/explanations.
      </p>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────── */
export default function ProductScannerInfoPage(): React.JSX.Element {
  const nav = useNavigate();
  const [tocOpen, setTocOpen] = useState(false);

  return (
    <div className="stack-6">

      {/* Sticky outline */}
      <div style={{ position: 'sticky', top: 'var(--s-4)', zIndex: 30, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => { setTocOpen(v => !v); }}
            className="btn btn--ghost btn--sm"
            aria-expanded={tocOpen}
          >
            Outline <IconChevron />
          </button>
          {tocOpen && (
            <div
              className="card card--bordered card--flat"
              style={{ position: 'absolute', right: 0, top: '100%', marginTop: 'var(--s-2)', width: '288px', padding: 'var(--s-3)', zIndex: 50 }}
            >
              <ul className="stack-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <TocLink href="#overview"  text="What the tools do"           onClick={() => { setTocOpen(false); }} />
                <TocLink href="#barcode"   text="Barcode resolution"          onClick={() => { setTocOpen(false); }} />
                <TocLink href="#datamodel" text="Data model & storage"        onClick={() => { setTocOpen(false); }} />
                <TocLink href="#dataset"   text="Dataset validation & COSING" onClick={() => { setTocOpen(false); }} />
                <TocLink href="#rules"     text="Rulesets & evidence"         onClick={() => { setTocOpen(false); }} />
                <TocLink href="#compat"    text="Compatibility engine"        onClick={() => { setTocOpen(false); }} />
                <TocLink href="#compare"   text="Comparison logic"            onClick={() => { setTocOpen(false); }} />
                <TocLink href="#analyze"   text="Single-product analysis"     onClick={() => { setTocOpen(false); }} />
                <TocLink href="#scoring"   text="Scoring & badges"            onClick={() => { setTocOpen(false); }} />
                <TocLink href="#prefs"     text="Personalization"             onClick={() => { setTocOpen(false); }} />
                <TocLink href="#evidence"  text="Auditability & versioning"   onClick={() => { setTocOpen(false); }} />
                <TocLink href="#limits"    text="Fair use & limits"           onClick={() => { setTocOpen(false); }} />
                <TocLink href="#privacy"   text="Privacy by design"           onClick={() => { setTocOpen(false); }} />
                <TocLink href="#faq"       text="FAQ"                         onClick={() => { setTocOpen(false); }} />
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Hero */}
      <motion.section
        className="card card--bordered card--flat"
        style={{ position: 'relative', overflow: 'hidden', padding: 0 }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
      >
        <div className="scan-pattern" aria-hidden="true" style={{ position: 'absolute', inset: 0, opacity: 0.45, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', padding: 'var(--s-5) var(--s-6)' }}>
          <div style={{ display: 'flex', gap: 'var(--s-4)', alignItems: 'flex-start' }}>
            <div style={{
              flexShrink: 0,
              width: 'var(--s-7)', height: 'var(--s-7)',
              borderRadius: 'var(--r-md)',
              background: 'var(--c-teal-weak)',
              color: 'var(--c-deep-teal)',
              display: 'grid', placeItems: 'center',
            }}>
              <IconBarcode />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 className="t-h2" style={{ margin: 0 }}>
                Product intelligence: <em className="t-italic t-teal">how it works.</em>
              </h1>
              <p className="t-body t-mute" style={{ marginTop: 'var(--s-3)', maxWidth: '60ch' }}>
                Scan a barcode. We resolve the product, standardize the ingredient list, apply domain rules with evidence,
                tune to your preferences, and present a recommendation you can understand.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', marginTop: 'var(--s-4)' }}>
                <span className="pill pill--teal"><IconStack />Clean data pipeline</span>
                <span className="pill pill--teal"><IconRule />Transparent rulesets</span>
                <span className="pill pill--teal"><IconTune />Preference-aware</span>
                <span className="pill pill--teal"><IconShield />Privacy-first</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', marginTop: 'var(--s-5)' }}>
                <button type="button" onClick={() => { nav('/scanner'); }} className="btn btn--ghost btn--sm">
                  <IconSpark /> Analyze a product
                </button>
                <button type="button" onClick={() => { nav('/comparison'); }} className="btn btn--ghost btn--sm">
                  <IconCompare /> Compare two products
                </button>
                <button type="button" onClick={() => { nav('/compatibility'); }} className="btn btn--ghost btn--sm">
                  <IconLink /> Check compatibility
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What the tools do */}
      <Section id="overview" title="What the tools do (at a glance)">
        <div className="g-3">
          <Explainer icon={<IconSpark />} title="Analyze" bullets={[
            'Resolve the product from its EAN/UPC/GTIN.',
            'Normalize ingredients (INCI-first) with flags and notes.',
            'Summaries in plain language with copyable facts.',
          ]} />
          <Explainer icon={<IconCompare />} title="Compare" bullets={[
            'Line up two products on what matters.',
            'Highlight trade-offs given your profile.',
            'Call ties when they are truly ties (with reasons).',
          ]} />
          <Explainer icon={<IconLink />} title="Compatibility" bullets={[
            'Order-aware (A sits under B).',
            'Context-aware (Auto / Leave-on / Leave-off).',
            'Evidence-weighted score with explanations.',
          ]} />
        </div>
      </Section>

      {/* Barcode */}
      <Section id="barcode" title="Barcode resolution (why it matters)">
        <div className="g-2">
          <InfoCard title="What we accept">
            <KivoList items={[
              'EAN-8, UPC-A, EAN-13, GTIN-14 (digits only; we strip spaces/symbols).',
              'We verify length and compute check-digits before any lookup.',
              'Manual entry works when cameras are unavailable.',
            ]} />
          </InfoCard>
          <InfoCard title="Why barcodes are powerful">
            <KivoList items={[
              'Stable identifiers across regions and retailers.',
              'Link variants and packshots to a canonical product record.',
              'Enable dedupe + change tracking across sources.',
            ]} />
          </InfoCard>
        </div>
        <div className="notice">
          EAN-13 check digit = <code style={{ fontFamily: 'ui-monospace, monospace' }}>(10 − (Σ(weighted digits) mod 10)) mod 10</code>. We validate first; only then do we query.
        </div>
      </Section>

      {/* Data model */}
      <Section id="datamodel" title="Data model & storage (what we keep, and why)">
        <div className="g-2">
          <InfoCard title="Core entities">
            <KivoList items={[
              'Products — canonical brand/name/category/image.',
              'Barcodes — GTIN aliases → product_id mapping.',
              'Ingredients — INCI-first registry with aliases (CAS/EC where applicable).',
              'ProductIngredients — join with original_text, position, confidence.',
              'EvidenceSources — provenance and freshness windows.',
              'Rules & RuleVersions — immutable snapshots with metadata.',
              'UserPrefs — avoid-list, sensitivity, breakout-prone, ethical, gentle actives.',
            ]} />
          </InfoCard>
          <InfoCard title="Design principles">
            <KivoList items={[
              'Canonicalize everything; keep raw alongside clean for audit.',
              'Immutability for trust — results reference a ruleset version + evidence snapshot.',
              'Explainability — every decision traces back to rules + signals + inputs.',
            ]} />
          </InfoCard>
        </div>
        <DataModelDiagram />
      </Section>

      {/* Dataset validation */}
      <Section id="dataset" title="Dataset validation & COSING mapping (how we keep it clean)">
        <div className="g-3">
          <Tile title="Source → Canonical">
            <p className="t-small t-mute" style={{ marginTop: 'var(--s-2)' }}>
              We ingest labels and reputable sources, then run a standardization pipeline:
              clean & tokenize → alias resolution → INCI canonicalization → keep a copy of the raw line for audit.
            </p>
          </Tile>
          <Tile title="COSING & identifiers">
            <KivoList items={[
              'Prefer INCI names; map synonyms/trade names to INCI.',
              'Where available, link CAS/EC identifiers for clarity.',
              'Regional variants normalized to a single canonical record.',
            ]} />
          </Tile>
          <Tile title="Validation & guardrails">
            <KivoList items={[
              'Check-digit validation on barcodes, schema checks on product facts.',
              'Ingredient strings must parse cleanly; low-confidence matches are flagged.',
              'If evidence is weak or conflicting, we choose conservative defaults or abstain.',
            ]} />
          </Tile>
        </div>
      </Section>

      {/* Rulesets */}
      <Section id="rules" title="Rulesets & evidence (transparent by design)">
        <div className="g-2">
          <InfoCard title="What a rule contains (conceptually)">
            <ul className="stack-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {([
                ['Scope', 'compatibility | compare | analyze'],
                ['Matcher', 'ingredient/category/context predicates (e.g., leave-on + AHA + retinoid)'],
                ['Effect', 'signal type (risk/benefit/note), weight, rationale'],
                ['Evidence', 'citations & freshness windows; conservative fallback if weak'],
              ] as [string, string][]).map(([label, detail]) => (
                <li key={label} style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
                  <span className="list-dot" />
                  <span className="t-small"><strong>{label}:</strong> {detail}</span>
                </li>
              ))}
            </ul>
          </InfoCard>
          <InfoCard title="Examples (not exhaustive)">
            <KivoList items={[
              'Acid layering: strong leave-on acids under retinoids → elevated irritation risk (order-sensitive).',
              'BPO with pure L-ascorbic: oxidation concerns → caution if layered simultaneously.',
              'Fragrance allergens: flagged when sensitivity or avoid-list settings are on.',
              'High-oil formulas + breakout-prone: nudge toward non-comedogenic alternatives.',
              'Photosensitizers by day: reminder note added (no penalty at night).',
            ]} />
          </InfoCard>
        </div>
      </Section>

      {/* Compatibility */}
      <Section id="compat" title="Compatibility engine (A → B)">
        <div className="g-2">
          <InfoCard title="Order & usage context">
            <KivoList items={[
              'Per product usage: Auto / Leave-on / Leave-off.',
              'Contexts combine (e.g., Leave-on → Leave-off) to tune thresholds.',
              'Signals are order-aware. Reversing A/B can change outcomes.',
            ]} />
          </InfoCard>
          <InfoCard title="What the output means">
            <KivoList items={[
              '0–100 score + Great/Mixed/Caution badge.',
              '"Abstain" when data is insufficient or conflicting.',
              'Explanations list the rules/ingredients that drove the result.',
            ]} />
          </InfoCard>
        </div>
      </Section>

      {/* Comparison */}
      <Section id="compare" title="Comparison logic (A vs B)">
        <div className="g-2">
          <InfoCard title="How we decide">
            <KivoList items={[
              'Align categories to avoid apples vs oranges.',
              'Weight ingredients & claims based on your profile.',
              'If it is a tie, we say so — and tell you why.',
            ]} />
          </InfoCard>
          <InfoCard title="Signals considered">
            <KivoList items={[
              'Actives and their likely roles (when responsibly inferable).',
              'Allergen/irritant presence & position.',
              'Category heuristics: cleanser vs leave-on vs rinse-off.',
            ]} />
          </InfoCard>
        </div>
      </Section>

      {/* Single-product analysis */}
      <Section id="analyze" title="Single-product analysis">
        <div className="g-2">
          <InfoCard title="What you get">
            <KivoList items={[
              'Product facts (brand, name, barcode, image).',
              'Normalized ingredients with flags/notes.',
              'Copyable barcode & ingredient string.',
            ]} />
          </InfoCard>
          <InfoCard title="Freshness & ingest">
            <KivoList items={[
              'First-time products may need a brief ingest before deep analysis.',
              'We cache canonical forms but keep the raw label for audit.',
            ]} />
          </InfoCard>
        </div>
      </Section>

      {/* Scoring */}
      <Section id="scoring" title="Scoring & badges (readable without hiding nuance)">
        <div className="g-3">
          <ScoreBadgeCard tone="great"   title="Great (≥70)"   desc="Signals are consistently positive or neutral for your profile." />
          <ScoreBadgeCard tone="mixed"   title="Mixed (40–69)" desc="Trade-offs exist; see the notes on what tipped the balance." />
          <ScoreBadgeCard tone="caution" title="Caution (<40)" desc="Multiple elevated concerns for your context; consider alternatives." />
        </div>
        <div className="notice" style={{ marginTop: 'var(--s-3)' }}>
          Scores are summaries. The explanation below them is the source of truth (which rules fired and why).
        </div>
      </Section>

      {/* Personalization */}
      <Section id="prefs" title="Personalization (optional, powerful)">
        <div className="g-3">
          <Tile title="Avoid-list & sensitivity" />
          <Tile title="Gentle actives mode" />
          <Tile title="Breakout-prone preference" />
          <Tile title="Ethical mode (vegan/halal/cruelty-free)" />
          <Tile title="Per-pair usage context (compatibility)" />
          <Tile title="Conservative defaults if unset" />
        </div>
      </Section>

      {/* Auditability */}
      <Section id="evidence" title="Auditability & versioning">
        <div className="g-2">
          <InfoCard title="Immutable rule snapshots">
            <KivoList items={[
              'Every result carries a ruleset version (e.g., v1.x).',
              'We can reproduce a past decision exactly by version.',
              'When rules improve, future runs use the new version.',
            ]} />
          </InfoCard>
          <InfoCard title="Evidence tracking">
            <KivoList items={[
              'Signals track source freshness and confidence.',
              'Weak or conflicting evidence → conservative outcomes or abstain.',
              'Precision over speculation — always.',
            ]} />
          </InfoCard>
        </div>
      </Section>

      {/* Credits */}
      <Section id="limits" title="How credits work">
        <div className="notice">
          Each action costs a small number of credits, shown in the tool headers along with your balance.
          Your plan grants monthly credits that refresh each billing period; you can also top up anytime with
          surplus credit packs. If you run out, credit-consuming actions are disabled with clear guidance to
          top up — keeping things fast and fair for everyone.
        </div>
      </Section>

      {/* Privacy */}
      <Section id="privacy" title="Privacy by design">
        <div className="g-2">
          <InfoCard title="What we use">
            <KivoList items={[
              'Barcodes and product facts to identify the item.',
              'Ingredient lists to run rules safely.',
              'Your optional preferences to tailor results to you.',
            ]} />
          </InfoCard>
          <InfoCard title="How we protect it">
            <KivoList items={[
              'Minimal, purpose-bound data — no unrelated tracking.',
              'No medical diagnoses or treatment claims.',
              'Clear controls to update or remove preferences.',
            ]} />
          </InfoCard>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" title="FAQ">
        <div className="stack-3">
          <FAQ q="Why does order matter in compatibility?" a="Some interactions are order-sensitive. A leave-on exfoliant under a retinoid is not the same as the reverse. We model A under B explicitly." />
          <FAQ q="What if an ingredient is listed oddly?" a="We keep the raw line, clean it, and map aliases to canonical names. If confidence is low, we will abstain or be conservative — not guess." />
          <FAQ q="Do you show your exact rules?" a="We describe the logic families and show which rules fired. The ruleset is versioned and explainable without exposing internal code." />
          <FAQ q="Is this medical advice?" a="No. We turn product data into clear, conservative guidance. For medical concerns, consult a professional." />
        </div>
      </Section>

      <p className="t-small t-mute" style={{ textAlign: 'center' }}>
        Updated as our rules evolve — clear signals, conservative defaults, your control.
      </p>
    </div>
  );
}

/* ── Building blocks ──────────────────────────────────── */

function TocLink({ href, text, onClick }: { href: string; text: string; onClick?: () => void }) {
  return (
    <li>
      <a
        href={href}
        onClick={onClick}
        className="t-small"
        style={{ display: 'block', borderRadius: 'var(--r-xs)', padding: '6px var(--s-2)', color: 'var(--ink)', transition: 'background var(--t-fast) var(--ease)' }}
        onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-sand)')}
        onMouseLeave={e => (e.currentTarget.style.background = '')}
      >
        {text}
      </a>
    </li>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="stack-3" style={{ scrollMarginTop: 'var(--s-9)' }}>
      <h2 className="t-h3">{title}</h2>
      {children}
    </section>
  );
}

function Explainer({ icon, title, bullets }: { icon?: React.ReactNode; title: string; bullets: string[] }) {
  return (
    <div className="card card--bordered card--flat" style={{ padding: 'var(--s-4)' }}>
      <div style={{ display: 'flex', gap: 'var(--s-3)', alignItems: 'flex-start' }}>
        <div style={{
          flexShrink: 0,
          width: 'var(--s-6)', height: 'var(--s-6)',
          borderRadius: 'var(--r-sm)',
          background: 'var(--c-teal-weak)',
          color: 'var(--c-deep-teal)',
          display: 'grid', placeItems: 'center',
        }}>
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="t-small" style={{ fontWeight: 600, color: 'var(--c-charcoal)' }}>{title}</div>
          <ul className="stack-2" style={{ listStyle: 'none', padding: 0, margin: 'var(--s-2) 0 0' }}>
            {bullets.map((b, i) => (
              <li key={i} style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
                <span className="list-dot" />
                <span className="t-small t-mute">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card card--bordered card--flat" style={{ padding: 'var(--s-4)' }}>
      <div className="t-small" style={{ fontWeight: 600, color: 'var(--c-charcoal)', marginBottom: 'var(--s-2)' }}>{title}</div>
      {children}
    </div>
  );
}

function KivoList({ items }: { items: string[] }) {
  return (
    <ul className="stack-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map((x, i) => (
        <li key={i} style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'flex-start' }}>
          <span className="list-dot" />
          <span className="t-small t-mute">{x}</span>
        </li>
      ))}
    </ul>
  );
}

function Tile({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="card card--bordered card--flat" style={{ padding: 'var(--s-3) var(--s-4)' }}>
      <div className="t-small" style={{ fontWeight: 600, color: 'var(--c-charcoal)' }}>{title}</div>
      {children && <div style={{ marginTop: 'var(--s-1)' }}>{children}</div>}
    </div>
  );
}

function ScoreBadgeCard({ tone, title, desc }: { tone: 'great' | 'mixed' | 'caution'; title: string; desc: string }) {
  const pillCls = tone === 'great' ? 'pill pill--ok' : tone === 'mixed' ? 'pill' : 'pill pill--warn';
  return (
    <div className="card card--bordered card--flat" style={{ padding: 'var(--s-4)' }}>
      <span className={pillCls}>{title}</span>
      <p className="t-small t-mute" style={{ marginTop: 'var(--s-2)' }}>{desc}</p>
    </div>
  );
}

function FAQ({ q, a }: { q: string; a: string }) {
  return (
    <details className="card card--bordered card--flat" style={{ padding: 'var(--s-4)' }}>
      <summary className="t-small" style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--c-charcoal)' }}>
        {q}
      </summary>
      <p className="t-small t-mute" style={{ marginTop: 'var(--s-2)' }}>{a}</p>
    </details>
  );
}
